import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { of as observableOf, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/response';
import { IAddWorkshops, IUpdateWorkshops } from './workshops-model';

@Injectable()
export class WorkshopsService {
    constructor(private http: HttpClient) {}

    private static get URL(): string {
        return `${environment.dashboardApi.baseUrl}${environment.dashboardApi.prefix}`;
    }
    private static readonly ENDPOINT =
        'events/{eventId}/eventpages/workshops/';

    /** GET event-level workshops (Workshop.EventId), independent of any page. Used to display the
     *  event's existing workshops when creating a workshops page. Returns { data: { workshops: [...] } }. */
    public getEventWorkshops(
        eventId: string
    ): Observable<SuccessResponse<any> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<any>>(
                WorkshopsService.URL + `events/${eventId}/workshops`
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }

    /** GET */
    public getWorkshops(
        eventId: string,
        id: string
    ): Observable<SuccessResponse<any> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<any>>(
                WorkshopsService.URL +
                WorkshopsService.ENDPOINT.replace('{eventId}', eventId) +
                    id
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }

    /** POST */
    public addWorkshops(
        eventId: string,
        workshopsAdd: any
    ): Observable<SuccessResponse<boolean> | ErrorResponse> {

        const formData: FormData = new FormData();

        workshopsAdd.workshops.forEach((workshop, index) => {
            formData.append(`workshops[${index}].index`, JSON.stringify(index));
            formData.append(`workshops[${index}].isActive`, workshop.isActive);

            if (workshop.workshopTitleTranslations && workshop.workshopTitleTranslations.length > 0) {
                workshop.workshopTitleTranslations.forEach((translation, index2) => {
                    formData.append(`workshops[${index}].workshopTitleTranslations[${index2}].languageId`,translation.languageId);
                    formData.append(`workshops[${index}].workshopTitleTranslations[${index2}].value`, translation.value);
                });
            }

            if (workshop.workshopSubtitleTranslations && workshop.workshopSubtitleTranslations.length > 0) {
                workshop.workshopSubtitleTranslations.forEach((translation, index2) => {
                    formData.append(`workshops[${index}].workshopSubtitleTranslations[${index2}].languageId`, translation.languageId);
                    formData.append(`workshops[${index}].workshopSubtitleTranslations[${index2}].value`, translation.value);
                });
            }

            if (workshop.workshopLocationTranslations && workshop.workshopLocationTranslations.length > 0) {
                workshop.workshopLocationTranslations.forEach((translation, index2) => {
                    formData.append(`workshops[${index}].workshopLocationTranslations[${index2}].languageId`, translation.languageId);
                    formData.append(`workshops[${index}].workshopLocationTranslations[${index2}].value`, translation.value);
                });
            }

            if (workshop.workshopDescriptionTranslations && workshop.workshopDescriptionTranslations.length > 0) {
                workshop.workshopDescriptionTranslations.forEach((translation, index2) => {
                    formData.append(`workshops[${index}].workshopDescriptionTranslations[${index2}].languageId`, translation.languageId);
                    formData.append(`workshops[${index}].workshopDescriptionTranslations[${index2}].value`, translation.value);
                });
            }

            if (workshop.speakerIds && workshop.speakerIds.length > 0) {
                workshop.speakerIds.forEach((speakerId, index2) => {
                    formData.append(`workshops[${index}].speakerIds[${index2}]`, speakerId);
                });
            }

            if (workshop.image) {
                formData.append(`workshops[${index}].image`, workshop.image);
            }
            if (workshop.id) {
                formData.append(`workshops[${index}].id`, workshop.id);
            }
        });

        workshopsAdd.contents.forEach((fileData, index) => {
            formData.append(`contents[${index}].languageId`,fileData.languageId);
            formData.append(`contents[${index}].value`, fileData.value);
        });

        workshopsAdd.titles.forEach((fileData, index) => {
            formData.append(`titles[${index}].languageId`,fileData.languageId);
            formData.append(`titles[${index}].value`, fileData.value);
        });

        const workshopsPageDisplayType = JSON.stringify(
            workshopsAdd.workshopsPageDisplayType
        );

        formData.append('workshopsPageDisplayType', workshopsPageDisplayType);
        // The create form preloads and submits the event's full workshop set, so the backend may remove
        // event-level workshops the user deleted (reconcile like edit). Older cached bundles omit this flag.
        formData.append('manageWorkshops', 'true');

        return this.http
            .post<SuccessResponse<boolean>>(
                WorkshopsService.URL +
                WorkshopsService.ENDPOINT.replace('{eventId}', eventId),
                formData
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }

    /** PUT */
    public updateWorkshops(
        eventId: string,
        id: string,
        workshopsUpdate: any
    ): Observable<SuccessResponse<boolean> | ErrorResponse> {

        const formData: FormData = new FormData();

        workshopsUpdate.workshops.forEach((workshop, index) => {
            formData.append(`workshops[${index}].index`, JSON.stringify(index));
            formData.append(`workshops[${index}].isActive`, workshop.isActive);

            if (workshop.workshopTitleTranslations && workshop.workshopTitleTranslations.length > 0) {
                workshop.workshopTitleTranslations.forEach((translation, index2) => {
                    formData.append(`workshops[${index}].workshopTitleTranslations[${index2}].languageId`,translation.languageId);
                    formData.append(`workshops[${index}].workshopTitleTranslations[${index2}].value`, translation.value);
                });
            }

            if (workshop.workshopSubtitleTranslations && workshop.workshopSubtitleTranslations.length > 0) {
                workshop.workshopSubtitleTranslations.forEach((translation, index2) => {
                    formData.append(`workshops[${index}].workshopSubtitleTranslations[${index2}].languageId`, translation.languageId);
                    formData.append(`workshops[${index}].workshopSubtitleTranslations[${index2}].value`, translation.value);
                });
            }

            if (workshop.workshopLocationTranslations && workshop.workshopLocationTranslations.length > 0) {
                workshop.workshopLocationTranslations.forEach((translation, index2) => {
                    formData.append(`workshops[${index}].workshopLocationTranslations[${index2}].languageId`, translation.languageId);
                    formData.append(`workshops[${index}].workshopLocationTranslations[${index2}].value`, translation.value);
                });
            }

            if (workshop.workshopDescriptionTranslations && workshop.workshopDescriptionTranslations.length > 0) {
                workshop.workshopDescriptionTranslations.forEach((translation, index2) => {
                    formData.append(`workshops[${index}].workshopDescriptionTranslations[${index2}].languageId`, translation.languageId);
                    formData.append(`workshops[${index}].workshopDescriptionTranslations[${index2}].value`, translation.value);
                });
            }

            if (workshop.speakerIds && workshop.speakerIds.length > 0) {
                workshop.speakerIds.forEach((speakerId, index2) => {
                    formData.append(`workshops[${index}].speakerIds[${index2}]`, speakerId);
                });
            }

            if (workshop.image) {
                formData.append(`workshops[${index}].image`, workshop.image);
            }
            if (workshop.imageUrl && !workshop.image) {
                formData.append(`workshops[${index}].imageUrl`, workshop.imageUrl);
            }
            if (workshop.id) {
                formData.append(`workshops[${index}].id`, workshop.id);
            }
        });

        workshopsUpdate.contents.forEach((fileData, index) => {
            formData.append(`contents[${index}].languageId`,fileData.languageId);
            formData.append(`contents[${index}].value`, fileData.value);
        });

        workshopsUpdate.titles.forEach((fileData, index) => {
            formData.append(`titles[${index}].languageId`,fileData.languageId);
            formData.append(`titles[${index}].value`, fileData.value);
        });

        const workshopsPageDisplayType = JSON.stringify(
            workshopsUpdate.workshopsPageDisplayType
        );

        formData.append('workshopsPageDisplayType', workshopsPageDisplayType);

        return this.http
            .put<SuccessResponse<boolean>>(
                WorkshopsService.URL +
                WorkshopsService.ENDPOINT.replace('{eventId}', eventId) +
                    id, formData
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }

    /** PUT */
    public enableWorkshops(
        eventId: string,
        id: string
    ): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(
                WorkshopsService.URL +
                WorkshopsService.ENDPOINT.replace('{eventId}', eventId) +
                    id +
                    '/activate',
                null
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }

    /** PUT */
    public disableWorkshops(
        eventId: string,
        id: string
    ): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(
                WorkshopsService.URL +
                WorkshopsService.ENDPOINT.replace('{eventId}', eventId) +
                    id +
                    '/deactivate',
                null
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }
}
